import { Banner } from "@design";
import { useQuery } from "@tanstack/react-query";
import { BellRing } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { matchPath, useLocation } from "react-router";
import { getPreferencesOptions } from "@/api";
import { BannerAction } from "@/components/BannerAction";
import { useDesktopPermission } from "@/features/settings/notifications/desktopPermission";
import { toRows } from "@/features/settings/notifications/notificationEvents";
import { useViewport } from "@/responsive/viewport";

const DISMISS_KEY = "anthm.desktopPermissionPromptDismissed";

function readDismissed() {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Desktop notification preferences live on the account, but the browser's permission doesn't: in a new browser
 * they're on yet nothing can show. Offer the permission prompt once (browsers only show it after a click).
 * Dismissal is remembered for this browser.
 */
export function DesktopPermissionBanner() {
  const { t } = useTranslation("notifications", { keyPrefix: "desktopPermission" });
  const { isMobile } = useViewport();
  const { permission, request } = useDesktopPermission();
  const { pathname } = useLocation();
  const onSettings = matchPath("/settings/notifications", pathname) !== null;
  const [dismissed, setDismissed] = useState(readDismissed);
  // Settings > Notifications shows the same offer inline.
  const eligible = !isMobile && permission === "default" && !dismissed && !onSettings;
  const preferences = useQuery({ ...getPreferencesOptions(), enabled: eligible });
  if (!eligible || !toRows(preferences.data?.data).some((row) => row.desktop)) return null;

  return (
    <Banner
      variant="info"
      icon={<BellRing size={14} />}
      message={t("message")}
      action={<BannerAction onClick={() => void request()}>{t("enable")}</BannerAction>}
      dismissLabel={t("dismiss")}
      onDismiss={() => {
        try {
          localStorage.setItem(DISMISS_KEY, "1");
        } catch {
          // Storage unavailable: still hide it for this page view.
        }
        setDismissed(true);
      }}
    />
  );
}
